import {
  getAllowedFileTypesByChannel,
  getMaxUploadSizeByChannel,
} from '@chatwoot/utils';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

// Brand names, deliberately not translated.
const CHANNEL_LABELS = {
  [INBOX_TYPES.WEB]: 'Website',
  [INBOX_TYPES.FB]: 'Facebook',
  [INBOX_TYPES.TWITTER]: 'X',
  [INBOX_TYPES.TWILIO]: 'Twilio',
  [INBOX_TYPES.WHATSAPP]: 'WhatsApp',
  [INBOX_TYPES.API]: 'API',
  [INBOX_TYPES.EMAIL]: 'Email',
  [INBOX_TYPES.TELEGRAM]: 'Telegram',
  [INBOX_TYPES.LINE]: 'LINE',
  [INBOX_TYPES.SMS]: 'SMS',
  [INBOX_TYPES.INSTAGRAM]: 'Instagram',
  [INBOX_TYPES.TIKTOK]: 'TikTok',
};

export const UNSUPPORTED_REASONS = {
  TYPE: 'TYPE',
  SIZE: 'SIZE',
};

const channelLabel = (channelType, medium) => {
  const base = CHANNEL_LABELS[channelType] || channelType;
  return medium ? `${base} (${medium})` : base;
};

/**
 * The channels this account can actually send through, deduplicated. A canned response is
 * account level, so its files may go out through any of them.
 */
export const getAccountChannels = (inboxes = []) => {
  const seen = new Map();

  inboxes.forEach(({ channel_type: channelType, medium }) => {
    if (!channelType) return;
    const key = `${channelType}:${medium || '*'}`;
    if (seen.has(key)) return;
    seen.set(key, {
      key,
      channelType,
      medium,
      label: channelLabel(channelType, medium),
    });
  });

  return [...seen.values()];
};

const matchesAllowedType = (allowedTypes, { contentType, filename }) => {
  const extension = `.${(filename || '').split('.').pop().toLowerCase()}`;

  return allowedTypes
    .split(',')
    .map(type => type.trim())
    .filter(Boolean)
    .some(type => {
      if (type === extension) return true;
      if (type.endsWith('/*')) return contentType.startsWith(type.slice(0, -1));
      return type === contentType;
    });
};

/**
 * Whether a file can be delivered through a given channel, and why not when it can't.
 * Mirrors the checks the composer runs at send time, so what we show here is what happens.
 */
export const checkAttachmentSupport = (
  { contentType, filename, byteSize },
  { channelType, medium },
  installationLimitMB
) => {
  // The web widget is not bound by channel rules, it only honours the installation limit.
  if (channelType === INBOX_TYPES.WEB) {
    return {
      supported: byteSize <= installationLimitMB * 1024 * 1024,
      reason: UNSUPPORTED_REASONS.SIZE,
      maxSizeMB: installationLimitMB,
    };
  }

  const allowedTypes = getAllowedFileTypesByChannel({ channelType, medium });
  if (!matchesAllowedType(allowedTypes, { contentType, filename })) {
    return { supported: false, reason: UNSUPPORTED_REASONS.TYPE };
  }

  const channelLimit = getMaxUploadSizeByChannel({
    channelType,
    medium,
    mime: contentType,
  });
  const maxSizeMB = Math.min(channelLimit, installationLimitMB);

  return {
    supported: byteSize <= maxSizeMB * 1024 * 1024,
    reason: UNSUPPORTED_REASONS.SIZE,
    maxSizeMB,
  };
};

/**
 * Compatibility of one file across every channel in the account.
 */
export const getAttachmentCompatibility = (
  attachment,
  channels,
  installationLimitMB
) =>
  channels.map(channel => ({
    ...channel,
    ...checkAttachmentSupport(attachment, channel, installationLimitMB),
  }));
